const SYSTEM_UI_PRINT_CONFIG_KEY = 'system_ui';

const safeObject = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

export const SYSTEM_ZOOM_OPTIONS = Object.freeze(
  Array.from({ length: 11 }, (_, index) => 50 + (index * 10))
);

export const SYSTEM_BREAKPOINTS = Object.freeze({
  mobileMax: 767,
  tabletMin: 768,
  tabletMax: 1279,
  desktopMin: 1280,
});

export const DEFAULT_SYSTEM_UI_CONFIG = Object.freeze({
  enableResponsiveZoom: false,
  mobileZoomPercent: 90,
  tabletZoomPercent: 90,
  desktopZoomPercent: 100,
  keepFontsLarge: false,
});

export const clampSystemZoomPercent = (value) => {
  const numericValue = Number.parseInt(String(value ?? ''), 10);
  if (!Number.isFinite(numericValue)) return 100;
  if (numericValue <= 50) return 50;
  if (numericValue >= 150) return 150;
  return Math.round(numericValue / 10) * 10;
};

const normalizeRawSystemUiConfig = (rawConfig) => ({
  enableResponsiveZoom:
    typeof rawConfig.enableResponsiveZoom === 'boolean'
      ? rawConfig.enableResponsiveZoom
      : DEFAULT_SYSTEM_UI_CONFIG.enableResponsiveZoom,
  mobileZoomPercent: clampSystemZoomPercent(
    rawConfig.mobileZoomPercent ?? DEFAULT_SYSTEM_UI_CONFIG.mobileZoomPercent,
  ),
  tabletZoomPercent: clampSystemZoomPercent(
    rawConfig.tabletZoomPercent ?? DEFAULT_SYSTEM_UI_CONFIG.tabletZoomPercent,
  ),
  desktopZoomPercent: clampSystemZoomPercent(
    rawConfig.desktopZoomPercent ?? DEFAULT_SYSTEM_UI_CONFIG.desktopZoomPercent,
  ),
  keepFontsLarge:
    typeof rawConfig.keepFontsLarge === 'boolean'
      ? rawConfig.keepFontsLarge
      : DEFAULT_SYSTEM_UI_CONFIG.keepFontsLarge,
});

export const getSystemUiConfigFromPrintConfig = (printConfig) => {
  const normalizedPrintConfig = safeObject(printConfig);
  const rawSystemUiConfig = safeObject(normalizedPrintConfig[SYSTEM_UI_PRINT_CONFIG_KEY]);
  return normalizeRawSystemUiConfig(rawSystemUiConfig);
};

export const mergeSystemUiConfigIntoPrintConfig = (printConfig, systemUiPatch) => {
  const normalizedPrintConfig = safeObject(printConfig);
  const currentSystemUiConfig = getSystemUiConfigFromPrintConfig(normalizedPrintConfig);
  const nextSystemUiConfig = normalizeRawSystemUiConfig({
    ...currentSystemUiConfig,
    ...safeObject(systemUiPatch),
  });
  return {
    ...normalizedPrintConfig,
    [SYSTEM_UI_PRINT_CONFIG_KEY]: nextSystemUiConfig,
  };
};

const SYSTEM_ZOOM_STYLE_ID = 'system-ui-zoom-style';
export const SYSTEM_ZOOM_TARGET_CLASS = 'system-zoom-target';
export const SYSTEM_KEEP_FONTS_CLASS = 'system-keep-fonts-large';
export const SYSTEM_UI_CHANGED_EVENT = 'impulza:system-ui-changed';

// Clases de tamano de texto mas usadas en la app. Para cada una generamos una
// regla de compensacion: font-size = basePx / zoomFactor. Asi cuando el wrapper
// aplica zoom < 1, las fuentes suben proporcionalmente y se ven al tamano original.
const TEXT_SIZE_PX_RULES = (() => {
  const arbitrary = [];
  // Enteros 4-50 (cubre desde badges minusculos hasta titulos grandes)
  for (let px = 4; px <= 50; px += 1) {
    arbitrary.push({ selector: `.text-\\[${px}px\\]`, basePx: px });
  }
  // Decimales medios (9.5, 10.5, ...) que aparecen en el diseno
  for (let half = 4.5; half <= 30.5; half += 1) {
    arbitrary.push({ selector: `.text-\\[${half}px\\]`, basePx: half });
  }
  return arbitrary;
})();

const TEXT_SIZE_NAMED_RULES = [
  { selector: '.text-xs', basePx: 12 },
  { selector: '.text-sm', basePx: 14 },
  { selector: '.text-base', basePx: 16 },
  { selector: '.text-lg', basePx: 18 },
  { selector: '.text-xl', basePx: 20 },
  { selector: '.text-2xl', basePx: 24 },
  { selector: '.text-3xl', basePx: 30 },
  { selector: '.text-4xl', basePx: 36 },
  { selector: '.text-5xl', basePx: 48 },
];

const buildFontCompensationCss = (zoomFactor) => {
  if (!Number.isFinite(zoomFactor) || zoomFactor === 1) return '';

  const rules = [...TEXT_SIZE_PX_RULES, ...TEXT_SIZE_NAMED_RULES];
  return rules
    .map(({ selector, basePx }) => {
      const compensated = Math.round((basePx / zoomFactor) * 100) / 100;
      return `.${SYSTEM_ZOOM_TARGET_CLASS}.${SYSTEM_KEEP_FONTS_CLASS} ${selector} { font-size: ${compensated}px !important; }`;
    })
    .join('\n');
};

export const applySystemUiZoomToDocument = (config) => {
  if (typeof document === 'undefined') return;

  const normalized = normalizeRawSystemUiConfig(safeObject(config));
  let styleEl = document.getElementById(SYSTEM_ZOOM_STYLE_ID);

  if (!normalized.enableResponsiveZoom) {
    if (styleEl) styleEl.textContent = '';
    return;
  }

  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = SYSTEM_ZOOM_STYLE_ID;
    document.head.appendChild(styleEl);
  }

  const mobile = normalized.mobileZoomPercent / 100;
  const tablet = normalized.tabletZoomPercent / 100;
  const desktop = normalized.desktopZoomPercent / 100;

  const mobileFontsCss = normalized.keepFontsLarge ? buildFontCompensationCss(mobile) : '';
  const tabletFontsCss = normalized.keepFontsLarge ? buildFontCompensationCss(tablet) : '';
  const desktopFontsCss = normalized.keepFontsLarge ? buildFontCompensationCss(desktop) : '';

  styleEl.textContent = `
    @media (max-width: ${SYSTEM_BREAKPOINTS.mobileMax}px) {
      .${SYSTEM_ZOOM_TARGET_CLASS} { zoom: ${mobile}; }
      ${mobileFontsCss}
    }
    @media (min-width: ${SYSTEM_BREAKPOINTS.tabletMin}px) and (max-width: ${SYSTEM_BREAKPOINTS.tabletMax}px) {
      .${SYSTEM_ZOOM_TARGET_CLASS} { zoom: ${tablet}; }
      ${tabletFontsCss}
    }
    @media (min-width: ${SYSTEM_BREAKPOINTS.desktopMin}px) {
      .${SYSTEM_ZOOM_TARGET_CLASS} { zoom: ${desktop}; }
      ${desktopFontsCss}
    }
  `;
};

export const broadcastSystemUiConfigChange = (config) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(SYSTEM_UI_CHANGED_EVENT, { detail: config }));
};
